import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Loader2 } from "lucide-react";
import axios from "axios";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * 
 * AuthCallback handles the Google OAuth callback.
 * It processes the session_id from the URL fragment and exchanges it for user data.
 */
const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const hasProcessed = useRef(false);

  const getErrorMessage = (error) => {
    const backendDetail = error?.response?.data?.detail;
    if (backendDetail) {
      return backendDetail;
    }

    if (error?.response?.status === 404) {
      return "No account was found for this Google email. Please sign in with an existing FlowGuard account or continue through the registration flow.";
    }

    if (error?.response?.status === 401) {
      return "Google sign-in could not be verified. Please try again.";
    }

    return "Authentication failed. Please try again.";
  };

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        const oauthIntent = sessionStorage.getItem("flowguard_google_oauth_intent") === "signup"
          ? "signup"
          : "login";
        const searchParams = new URLSearchParams(location.search);
        const oauthError = searchParams.get("oauth_error");
        if (oauthError) {
          navigate("/login", {
            replace: true,
            state: { error: oauthError },
          });
          return;
        }

        const googleSuccess = searchParams.get("google");
        if (googleSuccess === "success") {
          const refreshResponse = await axios.post(
            `${API}/auth/refresh`,
            {},
            { withCredentials: true }
          );

          if (refreshResponse.data.access_token && refreshResponse.data.user) {
            loginWithToken(refreshResponse.data.access_token, refreshResponse.data.user);
            sessionStorage.removeItem("flowguard_google_oauth_intent");
            navigate("/dashboard", { replace: true });
            return;
          }
        }

        // Get session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace("#", ""));
        const sessionId = params.get("session_id");

        if (!sessionId) {
          console.error("No session_id found in URL");
          navigate("/login", {
            replace: true,
            state: { error: "We could not verify your Google sign-in. Please try again." }
          });
          return;
        }

        const response = await axios.post(
          `${API}/auth/google/session`,
          { session_id: sessionId, intent: oauthIntent },
          { withCredentials: true }
        );

        if (response.data.access_token && response.data.user) {
          // Store token and user data without sending a password login request
          loginWithToken(response.data.access_token, response.data.user);
          sessionStorage.removeItem("flowguard_google_oauth_intent");

          // Clear the URL fragment and redirect to dashboard
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/dashboard", {
            replace: true,
            state: { user: response.data.user }
          });
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        const oauthIntent = sessionStorage.getItem("flowguard_google_oauth_intent") === "signup"
          ? "signup"
          : "login";
        sessionStorage.removeItem("flowguard_google_oauth_intent");
        navigate(oauthIntent === "signup" ? "/register" : "/login", {
          replace: true,
          state: { error: getErrorMessage(error) }
        });
      }
    };

    processAuth();
  }, [navigate, loginWithToken, location.search]);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
        <p className="text-white text-lg">Completing sign in...</p>
        <p className="text-slate-400 text-sm mt-2">Please wait while we verify your account.</p>
      </div>
    </div>
  );
};

export default AuthCallback;
